import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDevice } from '@/hooks';
import { PageSpinner } from '@/components/common/Spinner/Spinner';
import { EmptyState } from '@/components/common/EmptyState/EmptyState';
import { Button } from '@/components/common/Button/Button';
import { formatTimeAgo, formatCoordinate } from '@/lib/formatters';
import type { AccountDeviceRespObj } from '@/api/models';

function DeviceCard({ device, onSelect }: { device: AccountDeviceRespObj; onSelect: () => void }) {
  const { yachtName, deviceProperties, gpsLocationData } = device;
  const name = yachtName?.yachtName || 'Unnamed Vessel';
  const serial = deviceProperties?.deviceSerialStr || '—';
  const type = deviceProperties?.deviceTypeStr || deviceProperties?.deviceType || '—';
  const hasGPS = gpsLocationData?.locationValid;

  return (
    <button
      onClick={onSelect}
      className="group w-full rounded-xl border border-slate-200 bg-white p-5 text-left transition-all hover:border-teal-400 hover:shadow-md dark:border-navy-700 dark:bg-navy-900 dark:hover:border-teal-600"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-ocean-50 dark:bg-ocean-900/30">
            <svg className="h-5 w-5 text-ocean-600 dark:text-ocean-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 group-hover:text-teal-700 dark:text-white dark:group-hover:text-teal-400">
              {name}
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {type} &middot; {serial}
            </p>
          </div>
        </div>
      </div>

      {/* GPS & Last Update */}
      <div className="mt-4 flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400">
        {hasGPS && (
          <span className="flex items-center gap-1">
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
            </svg>
            {formatCoordinate(gpsLocationData.latitude, gpsLocationData.longitude)}
          </span>
        )}
        {gpsLocationData?.updatedDT && (
          <span className="flex items-center gap-1">
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {formatTimeAgo(gpsLocationData.updatedDT)}
          </span>
        )}
      </div>

      {/* Arrow indicator */}
      <div className="mt-3 flex justify-end">
        <svg className="h-4 w-4 text-slate-300 transition-transform group-hover:translate-x-1 group-hover:text-teal-500 dark:text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </button>
  );
}

export function DeviceListPage() {
  const { devices, devicesLoading, devicesError, selectDevice, refreshDevices } = useDevice();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('');

  const handleSelect = (deviceUUID: string) => {
    selectDevice(deviceUUID);
    navigate(`/device/${deviceUUID}/tracking`);
  };

  if (devicesLoading && devices.length === 0) {
    return <PageSpinner />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">My Devices</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {devices.length} vessel{devices.length !== 1 ? 's' : ''} connected to your account
          </p>
        </div>
        <div className="flex items-center gap-2">
          {devices.length > 1 && (
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter vessels…"
              className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 placeholder:text-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 dark:border-navy-600 dark:bg-navy-800 dark:text-slate-200 dark:placeholder:text-slate-500"
            />
          )}
          <Button variant="secondary" size="sm" onClick={() => refreshDevices()} loading={devicesLoading}>
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
          </svg>
            Refresh
          </Button>
        </div>
      </div>

      {devicesError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {devicesError}
        </div>
      )}

      {devices.length === 0 && !devicesLoading ? (
        <EmptyState
          title="No devices found"
          description="No vessels are associated with your account. Contact support if you believe this is an error."
          icon={
            <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
          {(filter
            ? devices.filter((d) =>
                (d.yachtName?.yachtName || d.deviceProperties.deviceSerialStr || '')
                  .toLowerCase()
                  .includes(filter.toLowerCase()),
              )
            : devices
          ).map((device) => (
            <DeviceCard
              key={device.deviceProperties.deviceUUID}
              device={device}
              onSelect={() => handleSelect(device.deviceProperties.deviceUUID)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
